import { ActivatedRoute, Router } from '@angular/router';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import {
	ApplicationContext,
	AuthnService,
	BreadcrumbsService,
	CompanyService,
	ComponentBase,
	DeploymentContext,
	StatusItem,
	TabInfo,
	TitleAndMetadata,
	UserService,
	WebAnalyticsService,
} from '@Common';
import {
	Company,
	LocalizedTextIds,
	isNewCompany,
	hasDealsWithJnJ,
	convertToValidWebsiteUrl,
	LocPrefix,
	TabConfig,
	urlIdentifierForCompany,
	Mode,
} from 'company-finder-common';
import { DownloadComponent } from './components/download/download.component.js';

@Component({
	selector: 'company-details',
	templateUrl: './company-details.component.html',
	styleUrls: ['./company-details.component.scss'],
	standalone: false,
})
export class CompanyDetailsComponent extends ComponentBase implements OnInit {
	public company: Company;
	public Priorities = Priority;
	public isFollowing = false;
	public TabConfigs = TabConfig;
	public tabs: TabInfo[] = [
		{
			// This value is used to make the tab highlight (the red line under the text)
			// the same approximate length as the text it is highlighting.
			// FUTURE: Consider a fontmetric, programmatic approach
			highlightWidth: 77,
			label: this.Loc(LocalizedTextIds.CompanyDetailsPublicInfo),
			value: TabConfig.PUBLIC,
		},
	];
	private _activeTab: TabInfo = this.tabs[0];

	public constructor(
		dc: DeploymentContext,
		private applicationContext: ApplicationContext,
		private route: ActivatedRoute,
		private router: Router,
		private authnService: AuthnService,
		private breadcrumbsService: BreadcrumbsService,
		private companyService: CompanyService,
		private userService: UserService,
		private webAnalyticsService: WebAnalyticsService,
		private titleAndMetadata: TitleAndMetadata
	) {
		super(dc);
	}

	public get isDownloadDocumentEnabled(): boolean {
		const documentGeneration =
			this._deploymentContext.documentGenerationConfig;
		return documentGeneration.documentDownloadEnabled || false;
	}

	public get showAdditionalContacts(): boolean {
		return this.showSiteHead || this.showJpal;
	}

	public get showSiteHead(): boolean {
		return (
			!!this.company.siteHead &&
			this.featureSwitches.companyDetailsSwitches.showSiteHead
		);
	}

	public get showJpal(): boolean {
		return this.isInternalView && this.company.jpalContacts?.length > 0;
	}

	public get showPrimaryAreaData(): boolean {
		return (
			this.sectorDetailsEnabled &&
			(this.company.primarySector?.length > 0 ||
				this.company.primarySubSector?.length > 0 ||
				this.company.primaryIndication?.length > 0 ||
				this.company.primarySectorDetail?.length > 0)
		);
	}

	// As of ADJQ-1934, primary sector can have multiple values. However,
	// sector data is not shown in Universal and JLABS only allows a single
	// sector. If either of these change, we will need to implement
	// ADJQ-1994 and rework this code to ensure it renders cleanly with
	// real multi-sector data
	public get primarySector(): string {
		return this.company.primarySector?.join(', ');
	}

	public get showSecondaryAreaData(): boolean {
		return (
			this.sectorDetailsEnabled &&
			(this.company.secondarySector?.length > 0 ||
				this.company.secondarySubSector?.length > 0 ||
				this.company.secondaryIndication?.length > 0 ||
				this.company.secondarySectorDetail?.length > 0)
		);
	}

	public get showSimpleTabUi(): boolean {
		return this.isInternalOrBardaView;
	}

	public get showStatus(): boolean {
		return this.isInternalView && (!!this.priority || this.hasDealsWithJnJ);
	}

	public get priorityClass(): string {
		switch (this.priority) {
			case Priority.low:
				return `grp2-status-container-priority-icon-low`;
			case Priority.medium:
				return `grp2-status-container-priority-icon-medium`;
			case Priority.high:
				return `grp2-status-container-priority-icon-high`;
		}
	}

	public get suppressStealthMode(): boolean {
		return (
			!this.isInternalView || !this.company || !this.company.isStealthMode
		);
	}

	public get sectorDetailsEnabled() {
		return this.featureSwitches.companyDetailsSwitches.showSectorDetails;
	}

	public get showLeadProductStage(): boolean {
		return (
			this.sectorDetailsEnabled &&
			(this.featureSwitches.enableLeadProductStage ||
				this.featureSwitches.includeInPublicView.leadProductStage) &&
			this.company.currentRdStage?.length > 0
		);
	}

	public get statusItems(): StatusItem[] {
		const items: StatusItem[] =
			this.company.statuses
				?.filter(
					(status) =>
						!(
							this._deploymentContext.companyStatusIcon &&
							status ===
								this._deploymentContext.statusForCompanyIcon
						)
				)
				.map(
					(status) =>
						new StatusItem(
							this._deploymentContext.getStatusDisplayName(
								status
							),
							this._deploymentContext.getStatusTooltip(status)
						)
				) ?? [];

		if (this.company.isQfcWinner) {
			items.push(
				new StatusItem(this.Loc(this.LocalizedTextIds.QFCAwardee))
			);
		}

		if (this.company.isBlueKnight) {
			items.push(
				new StatusItem(
					this.Loc(this.LocalizedTextIds.BlueKnight),
					null,
					'/assets/detail/blue-knight.png',
					18,
					14
				)
			);
		}

		return items;
	}

	public get companyStatuses(): string[] {
		return this._deploymentContext.getStatusDisplayNames(
			this.company.statuses
		);
	}

	public get internalHeader(): string {
		switch (this._activeTab.value) {
			case TabConfig.JNJ_ONLY:
				return this.Loc(LocalizedTextIds.CompanyDetailsInternalJJ);
			case TabConfig.BLUE_KNIGHT:
			case TabConfig.BLUE_KNIGHT_MILESTONES:
				return this.Loc(LocalizedTextIds.CompanyDetailsInternalJJBARDA);
			default:
				return '';
		}
	}

	public get internalSubtitle(): string {
		switch (this._activeTab.value) {
			case TabConfig.JNJ_ONLY:
				return this.Loc(
					LocalizedTextIds.CompanyDetailsInformationIntended
				);
			case TabConfig.BLUE_KNIGHT:
			case TabConfig.BLUE_KNIGHT_MILESTONES:
				return this.Loc(
					LocalizedTextIds.CompanyDetailsInformationIntendedBARDA
				);
			default:
				return '';
		}
	}

	public internalTabs: TabConfig[] = [
		TabConfig.JNJ_ONLY,
		TabConfig.BLUE_KNIGHT,
		TabConfig.BLUE_KNIGHT_MILESTONES,
	];

	public get showInternalHeader(): boolean {
		return this.internalTabs.includes(this._activeTab.value);
	}

	public async downloadWordDocument(company: Company): Promise<void> {
		this.hideDownloadMenu();
		const response = await this.companyService.getWordData(company);
		this.applicationContext.saveDocument(company.name, response, 'docx');
		this.webAnalyticsService.trackEvent('export-company-details-as-word', {
			category: 'Export',
			label: company.name,
		});
	}

	public async downloadPdf(company: Company): Promise<void> {
		this.hideDownloadMenu();
		const response = await this.companyService.getPdfData(company);
		this.applicationContext.saveDocument(company.name, response, 'pdf');
		this.webAnalyticsService.trackEvent('export-company-details-as-pdf', {
			category: 'Export',
			label: company.name,
		});
	}

	public handleTabClick(tab: TabInfo): void {
		this._activeTab = tab;
	}

	public isShowingTabContent(value: TabConfig): boolean {
		return this._activeTab.value === value;
	}

	public async setFollowing(isFollowing: boolean): Promise<void> {
		this.isFollowing = isFollowing;
		return this.userService.setFollowingCompany(
			this.company.id,
			isFollowing
		);
	}

	public get encodedName(): string {
		return urlIdentifierForCompany(this.company.name);
	}

	public async ngOnInit(): Promise<void> {
		this.company = this.route.snapshot.data.company;

		// We don't have a full 404 set up, this is just to protect against a user pasting in the URL of a
		// company that they are not allowed to see
		if (!this.company) {
			this.router.navigate(['/']);
			return;
		}

		this.titleAndMetadata.setPageTitle(this.company.name);

		const primaryAreas = [
			this.company.primarySector,
			this.company.primarySubSector,
			this.company.primaryIndication,
			this.company.primarySectorDetail,
		].filter((el) => el);
		const locationSummary = this.company.locations.reduce(
			(displayString, location) => {
				if (displayString.length > 0) {
					displayString += this.Loc(
						LocalizedTextIds.CompanyDetailsAnd
					);
				}

				// ADJQ-1654 -- This is for the page metadata. Determine what J&J would like done with this in Universal
				if (location.locationStatuses?.includes('Current')) {
					if (
						location.name ===
						this.Loc(LocalizedTextIds.CompanyDetailsVirtual)
					) {
						displayString += this.Loc(
							LocalizedTextIds.CompanyDetailsAVirtualCurrent
						);
					} else {
						displayString += this.Loc(
							LocalizedTextIds.CompanyDetailsACurrent,
							location.name
						);
					}
				} else {
					// this will always be "Alumni" for now.
					const locStatus =
						this.LocWithPrefix(
							location.locationStatuses
								? location.locationStatuses[0]
								: '',
							LocPrefix.LocStatus
						)?.toLocaleLowerCase() ?? '';
					if (
						location.name ===
						this.Loc(LocalizedTextIds.CompanyDetailsVirtual)
					) {
						displayString += this.Loc(
							LocalizedTextIds.CompanyDetailsAVirtual,
							locStatus
						);
					} else {
						displayString += this.Loc(
							LocalizedTextIds.CompanyDetailsAnOf,
							locStatus,
							location.name
						);
					}
				}

				return displayString;
			},
			''
		);

		this.titleAndMetadata.upsertMetaTagDescription(
			this.Loc(
				LocalizedTextIds.CompanyDetailsTheCompany,
				this.company.name,
				locationSummary,
				primaryAreas
					.reverse()
					.join(this.Loc(LocalizedTextIds.CompanyDetailsWithin))
			)
		);

		this.breadcrumbsService.setDetailsBreadcrumb(
			'company-detail',
			this.company.name,
			Mode.Company
		);
		this.isFollowing =
			this.isInternalView &&
			(await this.userService.isFollowingCompany(this.company.id));

		if (this.isInternalView) {
			this.tabs.push({
				highlightWidth: 145,
				label: this.Loc(LocalizedTextIds.CompanyDetailsInternalJJInfo),
				lockIcon: true,
				value: TabConfig.JNJ_ONLY,
			} as TabInfo);
		}

		if (this.isInternalOrBardaView) {
			if (
				this.company.isBlueKnight &&
				this.featureSwitches.enableBlueKnight
			) {
				this.tabs.push({
					blueKnightIcon: true,
					highlightWidth: 242,
					label: this.Loc(
						LocalizedTextIds.CompanyDetailsInternalBKOverview
					),
					lockIcon: true,
					value: TabConfig.BLUE_KNIGHT,
				} as TabInfo);
				this.tabs.push({
					blueKnightIcon: true,
					highlightWidth: 252,
					label: this.Loc(
						LocalizedTextIds.CompanyDetailsInternalBKMilestones
					),
					lockIcon: true,
					value: TabConfig.BLUE_KNIGHT_MILESTONES,
				} as TabInfo);
			}
		}
	}

	@HostListener('window:click')
	protected onClick(): void {
		this.hideDownloadMenu();
	}

	@ViewChild(DownloadComponent) downloadButton: DownloadComponent;

	public hideDownloadMenu(): void {
		this.downloadButton.showMenu = false;
	}

	public get tagsSorted(): string[] {
		return this.company.tags.sort((a, b) =>
			a.localeCompare(b, undefined, { sensitivity: 'base' })
		);
	}

	public get isNew(): boolean {
		return isNewCompany(this.company);
	}

	public get hasDealsWithJnJ(): boolean {
		return hasDealsWithJnJ(this.company);
	}

	public get isFollowEnabled(): boolean {
		return this.featureSwitches.enableFollowCompanies;
	}

	public get isInternalView(): boolean {
		return this.authnService.isInternal;
	}

	public get isInternalOrBardaView(): boolean {
		return this.authnService.isInternalOrBARDA;
	}

	/** This influences a conditional style for iconography that is currently IGNORED due to commit:
	 * Revision: fa9db4d01a910213c3f4f72756380f9fdc5c98db
	 * Author: Chris Belanger <cbelange@its.jnj.com>
	 * Date: 5/29/2024 4:23:28 PM
	 * Message: Feedback from demo
	 */
	public get priority(): Priority {
		// Current values & counts as of Oct 2025 are:
		// Near-Term: 135
		// Mid-Term: 288
		// Not Currently Identified: 817
		if (!this.company.priority) {
			return null;
		} else if (this.company.priority.match(/long/gi)) {
			return Priority.low;
		} else if (this.company.priority.match(/mid/gi)) {
			return Priority.medium;
		} else if (this.company.priority.match(/near/gi)) {
			return Priority.high;
		} else {
			return null;
		}
	}

	public get priorityDisplay(): string {
		return this.company.priority;
	}

	public get jForceUrlForProgessUpdate(): string {
		return `${
			this._deploymentContext.rawConfig.jforceIntegration.jforceUrl
		}/${this.getJForceIDFromDataLakeID(
			this.company.modelId
		)}?srPos=1&srKp=006`;
	}

	public get jForceUrlForOrgLevel(): string {
		return `${
			this._deploymentContext.rawConfig.jforceIntegration.jforceUrl
		}/${this.getJForceIDFromDataLakeID(this.company.id)}`;
	}

	public get validCompanyWebsiteUrl(): string {
		return convertToValidWebsiteUrl(this.company.website);
	}

	/** *************************************************************************
	 * FUTURE: Code below here can be removed are commented out once we remove
	 * the diagnostics dump.  Or maybe we enable that as a query string parameter...
	 */
	public get properties(): string[] {
		if (!this.company) {
			return [];
		}
		const props: string[] = [];
		Object.keys(this.company).forEach((key) => {
			props.push(key);
		});
		return props;
	}

	public isArrayValue(prop: string): boolean {
		return Array.isArray(this.company[prop]);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public getDisplayForItem(prop: string, item: any): string {
		switch (prop) {
			case 'locations':
				return this.getDisplayForValue(item.location);
			case 'tags':
			default:
				return this.getDisplayForValue(item);
		}
	}

	public getDisplayForValue(item: unknown): string {
		if (item instanceof Object) {
			return JSON.stringify(item).replace(/","/gi, '", "');
		} else {
			return item as string;
		}
	}

	/**
	 * The datalake ETL appends 2 characters to the end of the JForce IDs.
	 * This strips those last 2 characters to extract the ID that can be used for JForce references.
	 */
	private getJForceIDFromDataLakeID(datalakeID: string): string {
		return datalakeID.substring(0, datalakeID.length - 3);
	}
}

enum Priority {
	low = 1,
	medium,
	high,
}
